<template>
  <main id="page" class="w-full">
    <background-red-concrete :class="$device.android || $device.ios ? 'mobile-margin' : ''">
      <div class="relative top-0" id="page_top" />
      <the-loading-screen class="z-50" />

      <the-language-switcher
        class="fixed top-0 right-0 z-40"
        :class="$store.state.videoOn ? ' button-effect video-on' : 'video-off'"
      />
      <the-language-switcher-screen
        v-if="$store.state.languageScreenIsOpen"
        :class="$store.state.videoOn ? '' : 'z-50'"
      />
      <play-button
        @audioChange="handleAudioChange"
        :soundOn="$store.state.soundOn"
        class="fixed top-0 right-0 z-40"
        :class="$store.state.videoOn ? ' button-effect video-on' : 'video-off'"
      />

      <audio hidden id="audio-player" loop>
        <source :src="sectionList[0] ? sectionList[0].audio : ''" type="audio/mpeg" />
        If you're reading this, audio isn't supported.
      </audio>

      <exhibit-mobile-scroll
        v-if="isMobileDevice"
        :sectionList="sectionList"
        @next-section="nextSection"
        @previous-section="previousSection"
      />

      <horizontal-scroll v-if="!isMobileDevice">
        <section
          :isMobileDevice="isMobileDevice"
          :id="section.id"
          v-for="(section, index) of sectionList"
          :key="section.id"
          class="section horizontal-section"
          :class="section.type == 'gallery' ? 'section-large' : section.beforeGallery ? 'before-gallery' : ''"
        >
          <component
            :is="`section-${section.type}`"
            :content="section"
            :animated="false"
            @next-section="nextSection(index)"
          />
        </section>
      </horizontal-scroll>
    </background-red-concrete>
    <div v-if="$store.state.videoOn" class="w-screen h-screen fixed top-0 video-container">
      <video-player
        :video="$store.state.videoCode"
        :show="$store.state.videoOn"
        @close="handleVideoChange(false, '')"
      />
    </div>
  </main>
</template>

<script>
import TheLoadingScreen from "../loading-screen/TheLoadingScreen";
import TheLanguageSwitcher from "../language-switcher/TheLanguageSwitcher";
import TheLanguageSwitcherScreen from "../language-switcher/TheLanguageSwitcherScreen";
import SectionText from "../sections/SectionText";
import SectionImageRedirect from "../sections/SectionImageRedirect";
import SectionTextRedirect from "../sections/SectionTextRedirect";
import SectionSound from "../sections/SectionSound";
import SectionGallery from "../sections/SectionGallery/SectionGallery";
import BackgroundRedConcrete from "../section-backgrounds/RedConcrete";
import HorizontalScroll from "./HorizontalScroll";
import PlayButton from "../play-button/PlayButton.vue";
import VideoPlayer from "../sections/SectionGallery/VideoPlayer";
import sections from "../../sections.js";
import { mapGetters } from "vuex";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ExhibitMobileScroll from "./ExhibitMobileScroll.vue";

export default {
  components: {
    TheLoadingScreen,
    TheLanguageSwitcher,
    TheLanguageSwitcherScreen,
    SectionText,
    SectionImageRedirect,
    SectionTextRedirect,
    SectionSound,
    BackgroundRedConcrete,
    SectionGallery,
    HorizontalScroll,
    PlayButton,
    ExhibitMobileScroll,
    VideoPlayer,
  },

  data() {
    return {
      sectionList: [],
      isMobileDevice: false,
    };
  },
  computed: {
    ...mapGetters({ isLoading: "loader/isLoading" }),
  },
  mounted() {
    this.$store.commit("handleAudioChange", false);
    this.isMobileDevice = this.$device.android || this.$device.ios;

    var audio = document.getElementById("audio-player");
    audio.pause();

    this.$store.commit("loader/changeActiveLoader", this.getExhibit());
    this.sectionList = sections[this.$route.fullPath.substring(1)];
    gsap.registerPlugin(ScrollTrigger);
    this.startFromTop();
  },

  watch: {
    $route() {
      this.$router.go();
      this.startFromTop();
      this.sectionList = sections[this.$route.fullPath.substring(1)];
    },
  },

  methods: {
    getExhibit() {
      switch (this.$route.fullPath.substring(1)) {
        case "exhibit_a":
          return "exhibitA";
        case "exhibit_b":
          return "exhibitB";
        case "exhibit_c":
          return "exhibitC";
        default:
          return "";
      }
    },

    handleVideoChange(value, video) {
      this.$store.commit("handleVideoChange", { value, video });

      if (!this.$store.state.soundOn) {
        this.handleAudioChange();
      }
    },

    handleAudioChange() {
      var audio = document.getElementById("audio-player");
      if (this.$store.state.soundOn) {
        audio.pause();
      } else {
        audio.play();
      }

      this.$store.commit("handleAudioChange", !this.$store.state.soundOn);
    },

    startFromTop() {
      setTimeout(() => {
        gsap.to(window, { scrollTo: "#page_top", duration: 0 });
      });
    },
    nextSection(index) {
      //scrolls to the next section's id
      if (this.$device.android || this.$device.ios) {
        document.getElementById(this.sectionList[index + 1].id).scrollIntoView({ behavior: "smooth" });
      } else {
        const panelsSection = document.getElementById("horizontal-desktop");
        const targetElem = document.getElementById(this.sectionList[index + 1].id);

        let sectionsWidth = 0;
        this.sectionList.forEach((section) => {
          let sectionEl = document.getElementById(section.id);
          sectionsWidth += sectionEl.offsetWidth;
        });

        const containerOffset =
          (panelsSection.offsetTop + targetElem.offsetLeft) * (sectionsWidth / (sectionsWidth - window.innerWidth));

        gsap.to(window, {
          scrollTo: {
            y: containerOffset,
            autoKill: false,
          },
          duration: 1,
        });
      }
    },
    previousSection(index) {
      //scrolls to the next section's id
      document
        .getElementById(this.sectionList[index - 1].id)
        .scrollIntoView({ inline: "start", block: "start", behavior: "smooth" });
    },

    scrollHorizontally(event) {
      if (Math.abs(event.deltaX) < Math.abs(event.deltaY)) {
        return;
      }
      event.preventDefault();
      var scrollPosition = window.scrollY + event.deltaX * 2 - event.deltaY * 2;
      window.scrollTo(0, scrollPosition);
    },
  },

  created() {
    window.addEventListener("mousewheel", this.scrollHorizontally, { passive: false });
  },

  destroyed() {
    window.removeEventListener("mousewheel", this.scrollHorizontally);
  },
};
</script>

<style lang="postcss" scoped>
.mobile-margin {
  margin-bottom: env(safe-area-inset-bottom);
}
.section {
  &--large {
    width: 100vw;
    color: white;
  }
}

.section {
  height: 100vh !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
  display: flex !important;
  min-width: 100vw;
}

.section-large {
  min-width: 1000px;
  overflow: hidden;
}

.button-effect {
  transition: all 2s;
}

.video-on {
  opacity: 0;
  transition: all 2s;
  pointer-events: none;
}

.video-off {
  transition: all 2s;
  opacity: 1;
  pointer-events: all;
}

.video-container {
  z-index: 1500;
}
</style>